// src/pages/Register.jsx

import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { getAuth} from "firebase/auth";
// import MobileValidation from "../components/MobileValidation";
import { Bounce, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { createUser, checkUsernameAvailability, findUser } from "../api/userApi";
import {
  CountrySelector,
  MobileCodeSelector,
} from "../components/Selectors/Country";
import config from "../config";
import GoogleSignUp from "../components/GoogleSignUp";
import { useAuth } from "../context/AuthContext";
import EmailLinkRequest from "../components/EmailLinkRequest";

// --- Main Register Page ---
const Register = () => {
  const {setUserData} = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isCredentialSubmitting, setIsCredentialSubmitting] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState({
    checking: false,
    message: "",
    suggestion: "",
  });
  const [initialData, setInitialData] = useState({
    email: window.localStorage.getItem('regEmail') || "",
    displayName: "",
    username: "",
    phoneNumber: "",
    mobileCode: "961",
    country: "LB",
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: initialData,
    mode: "onBlur",
  });


  // whenever initialData changes (after provider success), reset the form defaults
  useEffect(() => {
    reset(initialData);
    setUsernameCheck({ checking: false, message: "", suggestion: "" });

    if (initialData.email || initialData.phoneNumber) {
      setStep("complete");
    }
  }, [initialData, reset]);
  const usernameValue = watch("username");

  useEffect(() => {
    setUsernameCheck({ checking: false, message: "", suggestion: "" });
  }, [usernameValue]);

  const handleUsernameBlur = async () => {
    const username = (usernameValue || "").trim();

    if (!username) {
      setUsernameCheck({ checking: false, message: "", suggestion: "" });
      return;
    }

    setUsernameCheck({ checking: true, message: "", suggestion: "" });

    try {
      const result = await checkUsernameAvailability(username);

      if (result?.valid) {
        setUsernameCheck({
          checking: false,
          message: t("usernameAvailable", { defaultValue: "Username is available." }),
          suggestion: "",
        });
      } else {
        setUsernameCheck({
          checking: false,
          message: t("usernameTaken", { defaultValue: "Username is already taken." }),
          suggestion: result?.suggestions || "",
        });
      }
    } catch (error) {
      setUsernameCheck({
        checking: false,
        message: error.message || t("usernameCheckError", { defaultValue: "Could not check username." }),
        suggestion: "",
      });
    }
  };

  const applyUsernameSuggestion = () => {
    if (!usernameCheck.suggestion) return;
    setValue("username", usernameCheck.suggestion, { shouldValidate: true, shouldDirty: true });
    setUsernameCheck({ checking: false, message: "", suggestion: "" });
  };

  const onCredentialChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const onCredentialsLogin = async (e) => {
    e.preventDefault();
    const username = credentials.username.trim();
    const password = credentials.password;

    if (!username || !password) {
      toast.error(
        t("usernamePasswordRequired", {
          defaultValue: "Username and password are required.",
        }),
        {
          position: "top-center",
          transition: Bounce,
        }
      );
      return;
    }

    try {
      setIsCredentialSubmitting(true);
      const loginUser = await findUser(username);

      if (!loginUser?.user_password) {
        throw new Error(
          t("passwordLoginNotAvailable", {
            defaultValue:
              "This account has no password set yet. Please continue with Google or Email Link.",
          })
        );
      }

      if (loginUser.user_password !== password) {
        throw new Error(
          t("invalidUsernamePassword", {
            defaultValue: "Invalid username or password.",
          })
        );
      }

      setUserData(loginUser);
      toast.success(
        t("loginsuccess", {
          defaultValue: "Login successfully!",
        }),
        {
          position: "top-center",
          transition: Bounce,
        }
      );
      navigate(`/`);
    } catch (err) {
      toast.error(
        err.message ||
          t("loginfail", {
            defaultValue: "Login failed. Please check your credentials.",
          }),
        {
          position: "top-center",
          autoClose: 3000,
          transition: Bounce,
        }
      );
    } finally {
      setIsCredentialSubmitting(false);
    }
  };

  const handleGoogleSuccess = ({
    email,
    displayName,
    username,
    phoneNumber,
  }) => {
    setInitialData({
      email,
      displayName,
      username,
      phoneNumber,
      mobileCode: "961",
      country: "LB",
    });
    setStep("complete");
    reset({
      email,
      displayName,
      username,
      phoneNumber,
      mobileCode: "961",
      country: "LB",
    });
  };

  const onProfileSubmit = async (data) => {
    try {
      const response = await fetch(`${config.apiUrl}/country`);
      const fetchountries = await response.json();

      const payload = {
        user_name: data.username,
        full_name: data.displayName,
        user_email: data.email,
        email_verified: true,
        mobile_verified: !!data.phoneNumber,
        country_code: data.mobileCode,
        user_mobile: data.mobile,
        provider: 1, // Google
        provider_code: getAuth().currentUser.uid,
        user_type: 2, // normal user
        languageId: data.languageId || 1,
        countryId:
          fetchountries.filter((country) => country.abbr === data.country)[0]
            ?.id || 1,
        gender: data.gender || null,
        dob: data.dob || null,
        bio: data.bio || null,
        website: data.website || null,
        complete_percentage: 50,
      };
      console.log(payload);
      const newUser = await createUser(payload);
      setUserData(newUser)
      toast.success(t("registeredSuccess"), {
        position: "top-center",
        transition: Bounce,
      });
      // navigate(`/profile/${newUser.id}`);
      navigate(`/`);
    } catch (err) {
      toast.error(err.message || t("registerError"), {
        position: "top-center",
        autoClose: 3000,
        transition: Bounce,
      });
    }
  };
  
  return (
    <>
 
      {!step && (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#120239]">
          <div className="bg-white p-8 rounded-3xl w-full max-w-md space-y-6 text-center">
            <div className="flex  flex-col justify-center items-center space-y-6 mb-10 sm:mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="167.4861 33.0851 178.1554 216.3149"
                className="h-20 w-20 "
              >
                <path
                  fill="#FF9200"
                  d="M 214.384 195.17 C 214.604 195.52 215.134 195.3 215.054 194.9 L 188.894 64.85 C 182.254 67.54 172.394 79.49 169.644 82.94 C 168.254 84.69 167.394 87.76 167.494 91.11 C 167.534 92.27 167.674 93.44 167.924 94.57 C 171.104 108.8 179.904 140.28 214.384 195.17 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#120239"
                  d="M 251.604 129.46 C 251.604 129.46 259.714 74.43 263.204 71.47 C 265.354 69.65 312.934 68.14 312.934 68.14 L 312.504 121.55 C 312.994 123.15 312.414 124.82 311.164 125.93 C 246.974 183.07 224.394 197.81 218.344 206.29 C 217.984 206.8 218.494 207.54 219.064 207.28 C 270.874 183.85 323.894 148.58 342.624 134.24 C 344.954 132.46 344.664 129.56 344.694 126.63 C 345.174 86.8 347.544 48.32 342.284 40.7 C 341.734 39.9 340.904 39.32 339.964 39.08 C 298.654 28.62 248.454 34.38 237.194 37.51 C 235.554 37.97 234.304 39.27 233.904 40.92 C 219.694 98.67 227.014 165.7 228.084 176.95 C 228.134 177.48 228.764 177.72 229.164 177.37 L 293.814 121.35 C 295.514 119.83 295.634 117.2 294.084 115.53 L 270.784 80.92 C 269.674 79.8 268.194 81.02 268.624 82.54 C 273.454 99.73 276.724 111.6 276.724 111.6 L 251.604 129.46 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#120239"
                  d="M 184.644 215.71 L 194.174 241.9 L 203.704 215.71 L 208.834 215.71 L 196.534 248.84 L 191.814 248.84 L 179.514 215.71 L 184.644 215.71 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#FF9200"
                  d="M 222.134 248.84 L 217.454 248.84 L 217.454 215.71 L 222.134 215.71 L 222.134 248.84 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#120239"
                  d="M 237.564 220.25 C 240.894 216.92 244.914 215.26 249.634 215.26 C 252.324 215.26 254.854 215.85 257.214 217.03 C 259.574 218.21 261.554 219.83 263.164 221.89 L 258.984 224.43 C 257.864 223.07 256.484 221.99 254.854 221.21 C 253.224 220.42 251.484 220.03 249.634 220.03 C 246.244 220.03 243.344 221.22 240.944 223.59 C 238.534 225.97 237.334 228.85 237.334 232.24 C 237.334 235.69 238.534 238.61 240.944 241 C 243.354 243.39 246.244 244.59 249.634 244.59 C 252.814 244.59 255.424 243.64 257.484 241.75 C 259.544 239.86 260.694 237.37 260.934 234.28 L 248.544 234.28 L 248.544 229.97 L 266.014 229.97 C 266.134 230.61 266.194 231.38 266.194 232.28 C 266.194 237.33 264.654 241.45 261.584 244.63 C 258.514 247.81 254.524 249.4 249.624 249.4 C 244.904 249.4 240.884 247.73 237.554 244.38 C 234.224 241.04 232.564 237 232.564 232.28 C 232.574 227.58 234.234 223.57 237.564 220.25 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#120239"
                  d="M 280.184 220.25 C 283.514 216.92 287.534 215.26 292.254 215.26 C 296.944 215.26 300.954 216.92 304.284 220.25 C 307.614 223.58 309.274 227.59 309.274 232.28 C 309.274 237 307.614 241.03 304.284 244.38 C 300.954 247.72 296.944 249.4 292.254 249.4 C 287.534 249.4 283.514 247.73 280.184 244.38 C 276.854 241.04 275.194 237 275.194 232.28 C 275.184 227.58 276.854 223.57 280.184 220.25 Z M 279.954 232.23 C 279.954 235.68 281.154 238.6 283.564 240.99 C 285.974 243.38 288.864 244.58 292.254 244.58 C 295.614 244.58 298.494 243.38 300.904 240.99 C 303.314 238.6 304.514 235.68 304.514 232.23 C 304.514 228.87 303.314 226 300.904 223.61 C 298.494 221.22 295.614 220.02 292.254 220.02 C 288.864 220.02 285.964 221.21 283.564 223.58 C 281.154 225.96 279.954 228.84 279.954 232.23 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
                <path
                  fill="#120239"
                  d="M 332.334 215.71 C 335.634 215.71 338.314 216.71 340.394 218.71 C 342.464 220.71 343.504 223.17 343.504 226.11 C 343.504 228.44 342.804 230.49 341.414 232.26 C 340.024 234.03 338.194 235.19 335.924 235.73 L 344.044 248.85 L 338.594 248.85 L 331.064 236.55 L 324.434 236.55 L 324.434 248.85 L 319.764 248.85 L 319.764 215.72 L 332.334 215.72 L 332.334 215.71 Z M 332.244 231.96 C 334.184 231.96 335.734 231.39 336.914 230.26 C 338.094 229.13 338.684 227.74 338.684 226.11 C 338.684 224.51 338.094 223.14 336.914 222 C 335.734 220.87 334.174 220.3 332.244 220.3 L 324.434 220.3 L 324.434 231.96 L 332.244 231.96 L 332.244 231.96 Z"
                  transform="matrix(1, 0, 0, 1, 7.105427357601002e-15, 0)"
                />
              </svg>
              <h1 className="text-xl max-[450px]:text-lg font-bold text-[#FF9200]">
                {t("connectAndCompete")}
              </h1>
            </div>
            {/* <MobileValidation onValidation={(phone) => setInitialData((d) => ({ ...d, phoneNumber: phone }))} /> */}
          
            <GoogleSignUp onSuccess={handleGoogleSuccess} />
            <hr></hr> {t("or")}
            <EmailLinkRequest />
            <div className="text-sm text-slate-500">{t("or")}</div>
            <form onSubmit={onCredentialsLogin} className="space-y-3 text-left">
              <input
                name="username"
                type="text"
                value={credentials.username}
                onChange={onCredentialChange}
                placeholder={t("username")}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
              />
              <input
                name="password"
                type="password"
                value={credentials.password}
                onChange={onCredentialChange}
                placeholder={t("password")}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
              />
              <button
                type="submit"
                disabled={isCredentialSubmitting}
                className="w-full bg-[#0a2942] text-white py-3 rounded-full disabled:opacity-50"
              >
                {isCredentialSubmitting ? (
                  <Loader2 className="animate-spin w-5 h-5 mx-auto" />
                ) : (
                  t("loginWithUsernamePassword", {
                    defaultValue: "Login with Username & Password",
                  })
                )}
              </button>
            </form>
            {/* <EmailSignUp onSuccess={(data) => setInitialData((d) => ({ ...d, ...data }))} /> */}
            {/* <p className="text-sm">
              {t("alreadyHaveAccount")} {" "}
              <a href="/login" className="text-[#47b5ff] font-bold">
                {t("signIn")}
              </a>
            </p> */}
            <ToastContainer />
          </div>
        </div>
      )}
      {step === "complete" && (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#120239]">
          <div className="bg-white p-8 rounded-3xl w-full max-w-lg space-y-6">
            <h2 className="text-2xl font-bold text-[#0a2942]">
              {t("completeProfile")}
            </h2>
            <form
              onSubmit={handleSubmit(onProfileSubmit)}
              className="space-y-4"
            >
              {/* Email */}
              <input
                type="email"
                placeholder={t("email")}
                {...register("email", {
                  required: t("emailreq"),
                  pattern: {
                    value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,6}$/,
                    message: t("emailval"),
                  },
                })}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
                readOnly
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email.message}</p>
              )}

              {/* Username */}
              <div className="space-y-2 ">
                <label htmlFor="displayName" className="text-sm font-medium px-3">
                  {t("fullName")}
                </label>
                <input
                  type="text"
                  placeholder={t("fullName")}
                  {...register("displayName", { required: t("usernamereq") })}
                  className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
                />
                {errors.displayName && (
                  <p className="text-xs text-red-500">
                    {errors.displayName.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium px-3">
                  {t("username")}
                </label>
              <input
                type="text"
                placeholder={t("username")}
                {...register("username", { required: t("usernamereq") })}
                onBlur={(e) => {
                  register("username", { required: t("usernamereq") }).onBlur(e);
                  handleUsernameBlur();
                }}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
              />
              {errors.username && (
                <p className="text-xs text-red-500">
                  {errors.username.message}
                </p>
              )}
              {!errors.username && usernameCheck.checking && (
                <p className="text-xs text-gray-500">
                  {t("checkingUsername", { defaultValue: "Checking username..." })}
                </p>
              )}
              {!errors.username && !usernameCheck.checking && usernameCheck.message && (
                <p className={`text-xs ${usernameCheck.suggestion ? 'text-red-500' : 'text-green-600'}`}>
                  {usernameCheck.message}
                </p>
              )}
              {!errors.username && usernameCheck.suggestion && (
                <button
                  type="button"
                  onClick={applyUsernameSuggestion}
                  className="text-xs font-medium text-[#47b5ff] underline"
                >
                  {t("useSuggestion", {
                    defaultValue: `Use suggestion: ${usernameCheck.suggestion}`,
                  })}
                </button>
              )}
              </div>
              <div className="relative">
                <div className="flex items-center justify-center gap-2">
                  <Controller
                    name="mobileCode"
                    control={control}
                    rules={{ required: t("codeRequired") }}
                    render={({ field, fieldState: { error } }) => (
                      <MobileCodeSelector {...field} error={error?.message} />
                    )}
                  />
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    // Filter out non-digit characters as the user types:
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    {...register("mobile", {
                      required: t("mobilereg"),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t("mobileval"),
                      },
                      minLength: {
                        value: 6,
                        message:
                          t("mobileminlength") || "Enter at least 6 digits",
                      },
                    })}
                    className={`w-full px-4 py-2 rounded-full bg-[#f0f9ff] border-none focus:ring-2 focus:ring-[#47b5ff] ${
                      errors.mobile ? "border-red-500" : ""
                    }`}
                    placeholder={t("entermobile")}
                  />
                </div>
                {errors.mobile && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.mobile.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: t("countryRequired") }}
                  render={({ field, fieldState: { error } }) => (
                    <CountrySelector {...field} error={error?.message} />
                  )}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="dob" className="text-sm font-medium px-3" >
                  {t("dateOfBirth")}
                </label>
              <input
                type="date"
                placeholder={t("dateOfBirth")}
                {...register("dob", { required: t("dobRequired") })}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
              />
              {errors.displayName && (
                <p className="text-xs text-red-500">
                  {errors.displayName.message}
                </p>
              )}
</div>
              <select
                {...register("gender", { required: t("genderReq") })}
                defaultValue={1}
                className="w-full p-3 rounded-full border focus:ring-2 focus:ring-[#47b5ff]"
              >
                <option value="">{t("selectGender")}</option>
                <option value="1">{t("male")}</option>
                <option value="2">{t("female")}</option>
              </select>
              {errors.gender && (
                <p className="text-xs text-red-500">{errors.gender.message}</p>
              )}
              {/* Terms */}
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register("terms", { required: t("tmval") })}
                  className="h-4 w-4"
                />
                <span className="text-sm">
                  {t("iagree")}{" "}
                  <a href="/terms" className="text-[#47b5ff]">
                    {t("termsConditions")}
                  </a>
                </span>
              </label>
              {errors.terms && (
                <p className="text-xs text-red-500">{errors.terms.message}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#ff8000] text-white py-3 rounded-full disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin w-5 h-5 mx-auto" />
                ) : (
                  t("register")
                )}
              </button>
            </form>
            <ToastContainer />
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
